// src/components/UserForm.jsx
import { useEffect, useState } from "react";

// Componente formulario para crear/editar usuario

export default function UserForm({
  onSubmit,
  editingUser = null,
  onCancel,
  loading = false,
}) {
  const isEditing = Boolean(editingUser);

  const [form, setForm] = useState({
    username: "",
    email: "",
    nombre: "",
    apellido: "",
    fecha_nacimiento: "",
    password: "",
  });

  const [changePassword, setChangePassword] = useState(false);
  const [error, setError] = useState("");

  // Prefill en modo edición
  useEffect(() => {
    if (isEditing) {
      setForm({
        username: editingUser.username ?? "",
        email: editingUser.email ?? "",
        nombre: editingUser.nombre ?? "",
        apellido: editingUser.apellido ?? "",
        fecha_nacimiento: editingUser.fecha_nacimiento
          ? String(editingUser.fecha_nacimiento).slice(0, 10)
          : "",
        password: "",
      });
      setChangePassword(false);
      setError("");
    } else {
      setForm({
        username: "",
        email: "",
        nombre: "",
        apellido: "",
        fecha_nacimiento: "",
        password: "",
      });
      setChangePassword(true); // crear requiere contraseña
      setError("");
    }
  }, [isEditing, editingUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validaciones rápidas
    if (!form.username || !form.email || !form.nombre || !form.apellido) {
      setError("Completa los campos obligatorios.");
      return;
    }
    if (!isEditing && !form.password) {
      setError("La contraseña es obligatoria al crear usuario.");
      return;
    }
    if (isEditing && changePassword && !form.password) {
      setError("Ingresa la nueva contraseña o desmarca 'Cambiar contraseña'.");
      return;
    }

    // Construye payload para el hook useUsers 
    const payload = {
      username: form.username,
      email: form.email,
      nombre: form.nombre,
      apellido: form.apellido,
      fecha_nacimiento: form.fecha_nacimiento || null,
      // En edición, solo incluir password si se quiere cambiar
      ...(isEditing
        ? changePassword && form.password
          ? { password: form.password }
          : {}
        : { password: form.password }),
    };

    try {
      await onSubmit(payload);
      if (!isEditing) {
        setForm({
          username: "",
          email: "",
          nombre: "",
          apellido: "",
          fecha_nacimiento: "",
          password: "",
        });
      }
    } catch (err) {
      setError(err.message || "No se pudo guardar el usuario");
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-xl font-bold mb-1">
        {isEditing ? "Editar usuario" : "Crear usuario"}
      </h2>
      <p className="text-sm text-slate-500 mb-4">
        {isEditing
          ? "Actualiza los datos del usuario seleccionado."
          : "Completa los datos para registrar un nuevo usuario."}
      </p>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Nombre *
          </label>
          <input
            type="text"
            name="nombre"
            value={form.nombre}
            onChange={handleChange}
            placeholder="Juan"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
          />
        </div>

        {/* Apellido */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Apellido *
          </label>
          <input
            type="text"
            name="apellido"
            value={form.apellido}
            onChange={handleChange}
            placeholder="Pérez"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Email *
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="correo@empresa.cl"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
          />
        </div>

        {/* Username */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Username *
          </label>
          <input
            type="text"
            name="username"
            value={form.username}
            onChange={handleChange}
            placeholder="jperez"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
          />
        </div>

        {/* Fecha de nacimiento */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Fecha de nacimiento
          </label>
          <input
            type="date"
            name="fecha_nacimiento"
            value={form.fecha_nacimiento}
            onChange={handleChange}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
          />
        </div>

        {/* Contraseña */}
        <div className="md:col-span-2">
          {isEditing ? (
            <>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                <input
                  type="checkbox"
                  className="rounded border-slate-300"
                  checked={changePassword}
                  onChange={(e) => setChangePassword(e.target.checked)}
                />
                Cambiar contraseña
              </label>
              <input
                type="password"
                name="password"
                placeholder="Nueva contraseña"
                value={form.password}
                onChange={handleChange}
                disabled={!changePassword}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300 disabled:bg-slate-100"
              />
              <p className="text-xs text-slate-500 mt-1">
                Déjalo vacío si no quieres cambiarla.
              </p>
            </>
          ) : (
            <>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Contraseña *
              </label>
              <input
                type="password"
                name="password"
                placeholder="********"
                value={form.password}
                onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 placeholder-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
              />
            </>
          )}
        </div>

        {/* Botones */}
        <div className="md:col-span-2 flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-medium px-4 py-2.5 transition-colors shadow-sm"
          >
            {loading ? "Guardando..." : isEditing ? "Guardar cambios" : "Crear usuario"}
          </button>

          {isEditing && (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium px-4 py-2.5 transition-colors"
            >
              Cancelar
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
